import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useEvents } from '../../hooks/useEvents';
import { useRequests } from '../../hooks/useRequests';
import { useAboutUs } from '../../hooks/useAboutUs';
import { useOrders } from '../../hooks/useOrders';
import { getStoredUser, logout } from '../../services/authService';
import { ROUTES } from '../../constants/api';
import { playSuccessChime, playSoftPop } from '../../utils/audio';
import FormInput from '../../components/ui/FormInput';
import Button from '../../components/ui/Button';

import './DashboardPage.css';

/* ---- SVG Icons ---- */
const IconPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconDelete = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconAlert = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const IconLogout = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const IconCalendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const IconMail = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const IconCheckCircle = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const IconEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconPhone = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="12"/>
    <line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);

const IconUpload = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const IconBriefcase = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

const IconUserContact = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconWallet = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
    <path d="M3 5v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-7"/>
    <path d="M16 12h.01"/>
  </svg>
);

const IconClipboardList = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>
  </svg>
);

const IconTag = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0L22 10V4a2 2 0 0 0-2-2h-2"/>
    <circle cx="7" cy="7" r="1.5" fill="currentColor" strokeWidth="0"/>
  </svg>
);

/* ==============================================================
   ADMIN MAIN DASHBOARD PAGE (EVENTS & REQUESTS)
   ============================================================== */
export default function DashboardPage() {
  const navigate = useNavigate();
  const user = getStoredUser() || { fullName: 'Admin', phone: '' };

  // Tab State: 'events' | 'requests'
  const [activeTab, setActiveTab] = useState('events');

  // Search filter for orders
  const [orderSearch, setOrderSearch] = useState('');

  /** Fixed popover for compact order row: { orderId, type, rect } */
  const [orderPopover, setOrderPopover] = useState(null);

  // Request Confirmation State
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  // Hooks for Events CRUD and Requests Management
  const eventsState = useEvents();
  const requestsState = useRequests();
  const aboutState = useAboutUs();
  const ordersState = useOrders();

  const filteredOrders = ordersState.orders.filter(order => {
    const searchLower = orderSearch.toLowerCase();
    const matchesPerson = order.person?.toLowerCase().includes(searchLower) || false;
    const matchesPhone1 = order.phone1?.toLowerCase().includes(searchLower) || false;
    const matchesPhone2 = order.phone2?.toLowerCase().includes(searchLower) || false;
    const matchesDesc = order.description?.toLowerCase().includes(searchLower) || false;
    const matchesEvents = Array.isArray(order.eventDtos) && order.eventDtos.some(e => e.title?.toLowerCase().includes(searchLower));
    return matchesPerson || matchesPhone1 || matchesPhone2 || matchesDesc || matchesEvents;
  });

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  useEffect(() => {
    if (!orderPopover) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOrderPopover(null);
    };
    const onScrollResize = () => setOrderPopover(null);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [orderPopover]);

  useEffect(() => {
    if (!orderPopover) return;
    const exists = ordersState.orders.some((o) => o.id === orderPopover.orderId);
    if (!exists) setOrderPopover(null);
  }, [orderPopover, ordersState.orders]);

  const openOrderPopover = (type, orderId, anchorEl) => {
    if (!anchorEl?.getBoundingClientRect) return;
    const r = anchorEl.getBoundingClientRect();
    setOrderPopover((prev) => {
      if (prev && prev.orderId === orderId && prev.type === type) return null;
      return {
        orderId,
        type,
        rect: { top: r.top, left: r.left, bottom: r.bottom, width: r.width, height: r.height },
      };
    });
    playSoftPop();
  };

  const handleConfirmRequestToggle = async () => {
    if (!pendingRequest) return;
    try {
      await requestsState.handleToggleIsRead(pendingRequest.id, pendingRequest.isRead);
      playSuccessChime();
    } catch (err) {
      console.error(err);
    } finally {
      setRequestModalOpen(false);
      setPendingRequest(null);
    }
  };

  /* Helper: format LocalDateTime array or ISO string to standard date */
  const formatDate = (dateVal) => {
    if (!dateVal) return '—';
    try {
      if (Array.isArray(dateVal)) {
        const [year, month, day] = dateVal;
        return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`;
      }
      const d = new Date(dateVal);
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    } catch {
      return '—';
    }
  };

  return (
    <div className="dashboard-layout">
      {/* ======== Sidebar ======== */}
      <aside className="dashboard-sidebar">
        <a href={ROUTES.MAIN} className="dashboard-sidebar__brand">
          <div className="dashboard-sidebar__logo">U</div>
          <span className="dashboard-sidebar__brand-name">Uday</span>
        </a>

        <ul className="dashboard-sidebar__menu">
          <li>
            <button
              className={`dashboard-sidebar__item-link ${activeTab === 'events' ? 'dashboard-sidebar__item-link--active' : ''}`}
              onClick={() => { playSoftPop(); setActiveTab('events'); }}
            >
              <IconCalendar />
              Tadbirlar
            </button>
          </li>
          <li>
            <button
              className={`dashboard-sidebar__item-link ${activeTab === 'requests' ? 'dashboard-sidebar__item-link--active' : ''}`}
              onClick={() => { playSoftPop(); setActiveTab('requests'); }}
            >
              <IconMail />
              So'rovlar
              {requestsState.unreadCount > 0 && (
                <span className="requests-badge-count">{requestsState.unreadCount}</span>
              )}
            </button>
          </li>
          <li>
            <button
              className={`dashboard-sidebar__item-link ${activeTab === 'about' ? 'dashboard-sidebar__item-link--active' : ''}`}
              onClick={() => { playSoftPop(); setActiveTab('about'); }}
            >
              <IconInfo />
              Biz haqimizda
            </button>
          </li>
          <li>
            <button
              className={`dashboard-sidebar__item-link ${activeTab === 'orders' ? 'dashboard-sidebar__item-link--active' : ''}`}
              onClick={() => { playSoftPop(); setActiveTab('orders'); }}
            >
              <IconBriefcase />
              Buyurtmalar
            </button>
          </li>
        </ul>

        <div className="dashboard-sidebar__footer">
          <div className="dashboard-sidebar__user">
            <div className="dashboard-sidebar__user-avatar">
              {user.fullName.charAt(0).toUpperCase()}
            </div>
            <div className="dashboard-sidebar__user-info">
              <span className="dashboard-sidebar__user-name">{user.fullName}</span>
              <span className="dashboard-sidebar__user-role">Administrator</span>
            </div>
          </div>
          <button className="dashboard-sidebar__item-link" onClick={handleLogout}>
            <IconLogout />
            Chiqish
          </button>
        </div>
      </aside>

      {/* ======== Main Area ======== */}
      <main className="dashboard-main" role="main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="dashboard-header__intro">
            <h1 className="dashboard-header__title">
              {activeTab === 'events' && 'Tadbirlarni boshqarish'}
              {activeTab === 'requests' && 'So\'rovlarni boshqarish'}
              {activeTab === 'about' && 'Biz haqimizda boshqaruvi'}
              {activeTab === 'orders' && 'Buyurtmalarni boshqarish'}
            </h1>
            <p className="dashboard-header__subtitle">
              {activeTab === 'events' && 'Platformadagi faol tadbirlar to\'plamini boshqaring.'}
              {activeTab === 'requests' && 'Mijozlar tomonidan yuborilgan tadbirlarga buyurtma berish so\'rovlari.'}
              {activeTab === 'about' && 'Platforma, kompaniya va jamoa haqidagi batafsil ma\'lumot kartalarini tahrirlang.'}
              {activeTab === 'orders' && 'Buyurtmalar, to\'lov hisob-kitoblari va ishlar ro\'yxatini shakllantiring.'}
            </p>
          </div>
          <div className="dashboard-header__mobile-tools">
            <div className="dashboard-user-chip" title={user.fullName}>
              <span className="dashboard-user-chip__avatar" aria-hidden="true">
                {user.fullName.charAt(0).toUpperCase()}
              </span>
              <span className="dashboard-user-chip__name">{user.fullName}</span>
            </div>
            <button type="button" className="dashboard-header__logout" onClick={handleLogout}>
              <IconLogout />
              <span>Chiqish</span>
            </button>
          </div>
        </header>

        {/* Global Error Banner */}
        {activeTab === 'events' && eventsState.error && !eventsState.activeModal && (
          <div className="dashboard-error-banner" role="alert">
            <IconAlert /> {eventsState.error}
          </div>
        )}
        {activeTab === 'requests' && requestsState.error && (
          <div className="dashboard-error-banner" role="alert">
            <IconAlert /> {requestsState.error}
          </div>
        )}
        {activeTab === 'about' && aboutState.error && !aboutState.activeModal && (
          <div className="dashboard-error-banner" role="alert">
            <IconAlert /> {aboutState.error}
          </div>
        )}
        {activeTab === 'orders' && ordersState.error && !ordersState.activeModal && (
          <div className="dashboard-error-banner" role="alert">
            <IconAlert /> {ordersState.error}
          </div>
        )}

        {/* Stats Row */}
        <section className="dashboard-stats">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card__icon-wrap">
              <IconCalendar />
            </div>
            <div className="dashboard-stat-card__info">
              <span className="dashboard-stat-card__value">{eventsState.events.length}</span>
              <span className="dashboard-stat-card__label">Tadbirlar soni</span>
            </div>
          </div>

          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card__icon-wrap" style={{ background: 'rgba(57, 181, 74, 0.1)', color: '#39b54a' }}>
              <IconMail />
            </div>
            <div className="dashboard-stat-card__info">
              <span className="dashboard-stat-card__value">
                {requestsState.totalElements}
              </span>
              <span className="dashboard-stat-card__label">Jami so'rovlar</span>
            </div>
          </div>

          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card__icon-wrap" style={{
              background: activeTab === 'about' ? 'rgba(184, 134, 11, 0.1)' : activeTab === 'orders' ? 'rgba(57, 181, 74, 0.1)' : 'rgba(214, 69, 69, 0.1)',
              color: activeTab === 'about' ? 'darkgoldenrod' : activeTab === 'orders' ? '#39b54a' : 'var(--clr-error)'
            }}>
              {activeTab === 'about' ? <IconInfo /> : activeTab === 'orders' ? <IconBriefcase /> : <IconAlert />}
            </div>
            <div className="dashboard-stat-card__info">
              <span className="dashboard-stat-card__value">
                {activeTab === 'about' ? aboutState.items.length : activeTab === 'orders' ? ordersState.orders.length : requestsState.unreadCount}
              </span>
              <span className="dashboard-stat-card__label">
                {activeTab === 'about' ? 'Ma\'lumotlar soni' : activeTab === 'orders' ? 'Buyurtmalar soni' : 'Yangi so\'rovlar'}
              </span>
            </div>
          </div>
        </section>

        {/* ==================== TAB: EVENTS ==================== */}
        {activeTab === 'events' && (
          <section className="dashboard-content-box">
            <div className="dashboard-content-box__header">
              <h2 className="dashboard-content-box__title">Tadbirlar ro'yxati</h2>
              <Button
                id="btn-open-add-event"
                variant="primary"
                size="md"
                iconLeft={<IconPlus />}
                onClick={eventsState.openAddModal}
              >
                Tadbir qo'shish
              </Button>
            </div>

            {eventsState.isLoading ? (
              <div className="dashboard-loading">
                <div className="dashboard-loading__spinner" />
                <p>Ma'lumotlar yuklanmoqda...</p>
              </div>
            ) : eventsState.events.length === 0 ? (
              <div className="dashboard-empty">
                <div className="dashboard-empty__icon">✨</div>
                <p className="dashboard-empty__text">Hozircha hech qanday tadbir mavjud emas. Yangi tadbir qo'shib boshlang!</p>
              </div>
            ) : (
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th style={{ width: '80px' }}>ID</th>
                      <th>Tadbir nomi</th>
                      <th>Yaratilgan sana</th>
                      <th style={{ width: '120px', textAlign: 'right' }}>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventsState.events.map((e, index) => (
                      <tr key={e.id || index}>
                        <td>
                          <span className="event-cell-id">#{e.id}</span>
                        </td>
                        <td>
                          <span className="event-cell-title">{e.title}</span>
                        </td>
                        <td>
                          <span className="event-cell-date">{formatDate(e.createdAt)}</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="event-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              className="btn-icon-action"
                              title="Tahrirlash"
                              onClick={() => eventsState.openEditModal(e)}
                            >
                              <IconEdit />
                            </button>
                            <button
                              className="btn-icon-action btn-icon-action--delete"
                              title="O'chirish"
                              onClick={() => eventsState.openDeleteModal(e)}
                            >
                              <IconDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ==================== TAB: REQUESTS ==================== */}
        {activeTab === 'requests' && (
          <section className="dashboard-content-box">
            <div className="dashboard-content-box__header">
              <h2 className="dashboard-content-box__title">Mijozlar so'rovlari</h2>
            </div>

            {requestsState.isLoading ? (
              <div className="dashboard-loading">
                <div className="dashboard-loading__spinner" />
                <p>So'rovlar yuklanmoqda...</p>
              </div>
            ) : requestsState.requests.length === 0 ? (
              <div className="dashboard-empty">
                <div className="dashboard-empty__icon">✉️</div>
                <p className="dashboard-empty__text">Hozircha hech qanday so'rov kelib tushmagan.</p>
              </div>
            ) : (
              <>
                <div className="events-table-wrapper">
                  <table className="events-table">
                    <thead>
                      <tr>
                        <th>Mijoz</th>
                        <th>Tadbir turlari</th>
                        <th>Sana</th>
                        <th>Xabar</th>
                        <th>Holati</th>
                        <th style={{ width: '120px', textAlign: 'right' }}>Amallar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requestsState.requests.map((r, index) => {
                        const hasEvents = Array.isArray(r.events) && r.events.length > 0;

                        return (
                          <tr key={r.id || index} className={!r.isRead ? 'table-row-unread' : ''}>
                            <td>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                <span className="event-cell-title">{r.fullName}</span>
                                <a href={`tel:${r.phone}`} className="request-phone-link">
                                  <IconPhone /> {r.phone}
                                </a>
                              </div>
                            </td>
                            <td>
                              {hasEvents ? (
                                <div className="request-events-badges-wrap">
                                  {r.events.map((ev) => (
                                    <span key={ev.id} className="request-event-badge-tag">
                                      {ev.title}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="event-cell-date">—</span>
                              )}
                            </td>
                            <td>
                              {r.isEventDateClear ? (
                                <span className="event-cell-date">{formatDate(r.eventDate)}</span>
                              ) : (
                                <span className="request-badge-pending">Aniq emas</span>
                              )}
                            </td>
                            <td>
                              <span className="request-message-text" title={r.text}>
                                {r.text}
                              </span>
                            </td>
                            <td>
                              {r.isRead ? (
                                <span className="request-status-badge request-status-badge--read">
                                  O'qilgan
                                </span>
                              ) : (
                                <span className="request-status-badge request-status-badge--new">
                                  Yangi
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="event-cell-actions" style={{ justifyContent: 'flex-end' }}>
                                <button
                                  className={`btn-icon-action ${r.isRead ? '' : 'btn-icon-action--active-success'}`}
                                  title={r.isRead ? "Yangi deb belgilash" : "O'qilgan deb belgilash"}
                                  disabled={requestsState.isToggling[r.id]}
                                  onClick={() => {
                                    playSoftPop();
                                    setPendingRequest(r);
                                    setRequestModalOpen(true);
                                  }}
                                >
                                  {r.isRead ? <IconEye /> : <IconCheckCircle />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {requestsState.totalPages > 1 && (
                  <div className="dashboard-pagination-wrap">
                    <button
                      className="pagination-btn"
                      disabled={requestsState.page === 0}
                      onClick={() => requestsState.fetchRequests(requestsState.page - 1)}
                    >
                      Oldingi
                    </button>
                    <span className="pagination-text">
                      {requestsState.page + 1} / {requestsState.totalPages}
                    </span>
                    <button
                      className="pagination-btn"
                      disabled={requestsState.page >= requestsState.totalPages - 1}
                      onClick={() => requestsState.fetchRequests(requestsState.page + 1)}
                    >
                      Keyingi
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* ==================== TAB: ABOUT ==================== */}
        {activeTab === 'about' && (
          <section className="dashboard-content-box">
            <div className="dashboard-content-box__header">
              <h2 className="dashboard-content-box__title">Biz haqimizda kartalari</h2>
              <Button
                id="btn-open-add-about"
                variant="primary"
                size="md"
                iconLeft={<IconPlus />}
                onClick={aboutState.openAddModal}
              >
                Karta qo'shish
              </Button>
            </div>

            {aboutState.isLoading ? (
              <div className="dashboard-loading">
                <div className="dashboard-loading__spinner" />
                <p>Ma'lumotlar yuklanmoqda...</p>
              </div>
            ) : aboutState.items.length === 0 ? (
              <div className="dashboard-empty">
                <div className="dashboard-empty__icon">✨</div>
                <p className="dashboard-empty__text">Hozircha hech qanday ma'lumot mavjud emas. Yangi karta qo'shib boshlang!</p>
              </div>
            ) : (
              <div className="about-cards-grid">
                {aboutState.items.map((item, index) => (
                  <div className="about-admin-card" key={item.id || index}>
                    {Array.isArray(item.mediaUrls) && item.mediaUrls.length > 0 && (
                      <div className="about-admin-card__img-wrap">
                        <img src={item.mediaUrls[0]} alt={item.title} className="about-admin-card__img" />
                        {item.mediaUrls.length > 1 && (
                          <span className="about-admin-card__badge">+{item.mediaUrls.length - 1} rasm</span>
                        )}
                      </div>
                    )}
                    <div className="about-admin-card__content">
                      <h3 className="about-admin-card__title">{item.title}</h3>
                      <p className="about-admin-card__desc">{item.text}</p>
                      <div className="about-admin-card__footer">
                        <span className="about-admin-card__date">
                          Yangilandi: {formatDate(item.updatedAt || item.createdAt)}
                        </span>
                        <div className="about-admin-card__actions">
                          <button
                            className="btn-icon-action"
                            title="Tahrirlash"
                            onClick={() => aboutState.openEditModal(item)}
                          >
                            <IconEdit />
                          </button>
                          <button
                            className="btn-icon-action btn-icon-action--delete"
                            title="O'chirish"
                            onClick={() => aboutState.openDeleteModal(item)}
                          >
                            <IconDelete />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ==================== TAB: ORDERS ==================== */}
        {activeTab === 'orders' && (
          <section className="dashboard-content-box">
            <div className="dashboard-content-box__header">
              <h2 className="dashboard-content-box__title">Tadbir buyurtmalari ro'yxati</h2>
              <Button
                id="btn-open-add-order"
                variant="primary"
                size="md"
                iconLeft={<IconPlus />}
                onClick={ordersState.openAddModal}
              >
                Buyurtma qo'shish
              </Button>
            </div>

            <div className="dashboard-search-bar" style={{ marginBottom: '24px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Mijoz ismi, raqami yoki tadbirlarni qidirish..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                style={{ maxWidth: '400px' }}
              />
            </div>

            {ordersState.isLoading ? (
              <div className="dashboard-loading">
                <div className="dashboard-loading__spinner" />
                <p>Ma'lumotlar yuklanmoqda...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="dashboard-empty">
                <div className="dashboard-empty__icon">💼</div>
                <p className="dashboard-empty__text">
                  {orderSearch ? 'Qidiruv natijasida hech narsa topilmadi.' : "Hozircha hech qanday buyurtma mavjud emas. Yangi buyurtma qo'shib boshlang!"}
                </p>
              </div>
            ) : (
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>Mijoz</th>
                      <th>Tanlangan tadbirlar</th>
                      <th>Sana</th>
                      <th>Hisob-kitob</th>
                      <th>Ishlar ro'yxati</th>
                      <th>Holat</th>
                      <th>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => {
                      const balance = order.totalPrice - (order.advancePayment || 0);
                      const isFullyPaid = balance <= 0;
                      const eventList = Array.isArray(order.eventDtos) ? order.eventDtos : [];
                      const todoList = Array.isArray(order.requiredThings) ? order.requiredThings : [];
                      const popOnRow = orderPopover != null && orderPopover.orderId === order.id;
                      const popType = popOnRow ? orderPopover.type : null;
                      return (
                        <tr key={order.id} className={order.isDone ? 'row-completed' : ''}>
                          <td>
                            <div className="order-cell-compact">
                              <span className="order-client-name order-client-name--ellipsis" title={order.person}>
                                {order.person}
                              </span>
                              <button
                                type="button"
                                className={`order-pop-trigger ${popType === 'client' ? 'order-pop-trigger--active' : ''}`}
                                title="Aloqa va mijoz"
                                aria-expanded={popType === 'client'}
                                aria-controls={`order-pop-${order.id}-client`}
                                onClick={(e) => openOrderPopover('client', order.id, e.currentTarget)}
                              >
                                <IconUserContact />
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="order-cell-compact">
                              {eventList.length > 0 ? (
                                <>
                                  <span className="order-events-short" title={eventList.map((e) => e.title).join(', ')}>
                                    {eventList[0]?.title}
                                    {eventList.length > 1 ? ` +${eventList.length - 1}` : ''}
                                  </span>
                                  <button
                                    type="button"
                                    className={`order-pop-trigger ${popType === 'events' ? 'order-pop-trigger--active' : ''}`}
                                    title="Barcha tadbirlar"
                                    aria-expanded={popType === 'events'}
                                    aria-controls={`order-pop-${order.id}-events`}
                                    onClick={(e) => openOrderPopover('events', order.id, e.currentTarget)}
                                  >
                                    <IconTag />
                                  </button>
                                </>
                              ) : (
                                <span className="todo-empty-label">—</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="order-date-label">
                              {order.eventDate ? formatDate(order.eventDate) : 'Sana kiritilmagan'}
                            </span>
                          </td>
                          <td>
                            <div className="order-cell-compact">
                              <span
                                className={`order-fin-one-liner ${isFullyPaid ? 'fin-value--paid' : 'fin-value--outstanding'}`}
                                title="To'liq hisob-kitobni ko'rish uchun ikonkani bosing"
                              >
                                Qoldiq: {balance.toLocaleString()} UZS
                              </span>
                              <button
                                type="button"
                                className={`order-pop-trigger ${popType === 'finance' ? 'order-pop-trigger--active' : ''}`}
                                title="Hisob-kitob"
                                aria-expanded={popType === 'finance'}
                                aria-controls={`order-pop-${order.id}-finance`}
                                onClick={(e) => openOrderPopover('finance', order.id, e.currentTarget)}
                              >
                                <IconWallet />
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="order-cell-compact">
                              {todoList.length > 0 ? (
                                <>
                                  <span className="order-todo-short" title="Ro'yxatni ochish">
                                    {todoList.length} ta vazifa
                                  </span>
                                  <button
                                    type="button"
                                    className={`order-pop-trigger ${popType === 'todos' ? 'order-pop-trigger--active' : ''}`}
                                    title="Ishlar ro'yxati"
                                    aria-expanded={popType === 'todos'}
                                    aria-controls={`order-pop-${order.id}-todos`}
                                    onClick={(e) => openOrderPopover('todos', order.id, e.currentTarget)}
                                  >
                                    <IconClipboardList />
                                  </button>
                                </>
                              ) : (
                                <span className="todo-empty-label">—</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                              <label className="toggle-switch" title="Holatni o'zgartirish">
                                <input
                                  type="checkbox"
                                  checked={!!order.isDone}
                                  onChange={() => ordersState.handleToggleDone(order)}
                                />
                                <span className="toggle-slider" />
                              </label>
                              <span className={`order-status-badge ${order.isDone ? 'status-badge--success' : 'status-badge--pending'}`}>
                                {order.isDone ? 'Bajarildi' : 'Kutilmoqda'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="table-actions">
                              <button
                                className="btn-icon-action"
                                title="Batafsil / Tahrirlash"
                                onClick={() => ordersState.openEditModal(order)}
                              >
                                <IconEdit />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>

      {/* ======== Modal: ADD EVENT ======== */}
      {eventsState.activeModal === 'add' && (
        <div className="modal-overlay" onClick={eventsState.closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={eventsState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Yangi tadbir qo'shish</h3>
              <p className="modal-subtitle">Tizimda ko'rinadigan yangi tadbir turini yarating.</p>
            </div>
            <form onSubmit={eventsState.handleAddEvent}>
              {eventsState.error && (
                <div className="modal-error">
                  <IconAlert /> {eventsState.error}
                </div>
              )}
              <div className="modal-body">
                <FormInput
                  id="event-title-add"
                  name="title"
                  label="Tadbir nomi"
                  value={eventsState.title}
                  onChange={(e) => eventsState.setTitle(e.target.value)}
                  placeholder="Masalan: Nikoh to'yi"
                  error={eventsState.fieldError}
                  disabled={eventsState.isSubmitting}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <Button variant="outline" size="md" onClick={eventsState.closeModal} disabled={eventsState.isSubmitting}>
                  Bekor qilish
                </Button>
                <Button id="btn-submit-add-event" type="submit" variant="primary" size="md" isLoading={eventsState.isSubmitting}>
                  Saqlash
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======== Modal: EDIT EVENT ======== */}
      {eventsState.activeModal === 'edit' && (
        <div className="modal-overlay" onClick={eventsState.closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={eventsState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Tadbirni tahrirlash</h3>
              <p className="modal-subtitle">Tanlangan tadbir nomini o'zgartiring.</p>
            </div>
            <form onSubmit={eventsState.handleEditEvent}>
              {eventsState.error && (
                <div className="modal-error">
                  <IconAlert /> {eventsState.error}
                </div>
              )}
              <div className="modal-body">
                <FormInput
                  id="event-title-edit"
                  name="title"
                  label="Tadbir nomi"
                  value={eventsState.title}
                  onChange={(e) => eventsState.setTitle(e.target.value)}
                  placeholder="Masalan: Nikoh to'yi"
                  error={eventsState.fieldError}
                  disabled={eventsState.isSubmitting}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <Button variant="outline" size="md" onClick={eventsState.closeModal} disabled={eventsState.isSubmitting}>
                  Bekor qilish
                </Button>
                <Button id="btn-submit-edit-event" type="submit" variant="primary" size="md" isLoading={eventsState.isSubmitting}>
                  Yangilash
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======== Modal: CONFIRM DELETE ======== */}
      {eventsState.activeModal === 'delete' && eventsState.selectedEvent && (
        <div className="modal-overlay" onClick={eventsState.closeModal}>
          <div className="modal-content modal-content--delete" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={eventsState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Tadbirni o'chirish</h3>
              <p className="modal-subtitle">Haqiqatdan ham <strong>"{eventsState.selectedEvent.title}"</strong> tadbirini o'chirib tashlamoqchimisiz? Bu amalni ortga qaytarib bo'lmaydi.</p>
            </div>
            {eventsState.error && (
              <div className="modal-error">
                <IconAlert /> {eventsState.error}
              </div>
            )}
            <div className="modal-footer">
              <Button variant="outline" size="md" onClick={eventsState.closeModal} disabled={eventsState.isSubmitting}>
                Bekor qilish
              </Button>
              <Button
                id="btn-submit-delete-event"
                variant="primary"
                size="md"
                isLoading={eventsState.isSubmitting}
                style={{ background: 'var(--clr-error)', boxShadow: 'none' }}
                onClick={eventsState.handleDeleteEvent}
              >
                Ha, o'chirilsin
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ======== Modal: CONFIRM REQUEST TOGGLE ======== */}
      {requestModalOpen && pendingRequest && (
        <div className="modal-overlay" onClick={() => { playSoftPop(); setRequestModalOpen(false); setPendingRequest(null); }}>
          <div className="modal-content modal-content--delete" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={() => { playSoftPop(); setRequestModalOpen(false); setPendingRequest(null); }}
              aria-label="Yopish"
            >
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Holatni o'zgartirish</h3>
              <p className="modal-subtitle">
                Haqiqatdan ham <strong>"{pendingRequest.fullName}"</strong> ning so'rovini{' '}
                <strong>{pendingRequest.isRead ? "Yangi (o'qilmagan)" : "O'qilgan"}</strong> deb belgilamoqchimisiz?
              </p>
            </div>
            <div className="modal-footer">
              <Button
                variant="outline"
                size="md"
                onClick={() => { playSoftPop(); setRequestModalOpen(false); setPendingRequest(null); }}
              >
                Bekor qilish
              </Button>
              <Button
                id="btn-confirm-toggle-read"
                variant="primary"
                size="md"
                onClick={handleConfirmRequestToggle}
                isLoading={requestsState.isToggling[pendingRequest.id]}
              >
                Tasdiqlash
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ======== Modal: ADD ABOUT US ======== */}
      {aboutState.activeModal === 'add' && (
        <div className="modal-overlay" onClick={aboutState.closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={aboutState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Ma'lumot kartasini qo'shish</h3>
              <p className="modal-subtitle">Biz haqimizda sahifasi uchun yangi sarlavha, tavsif va rasmlar to'plamini kiriting.</p>
            </div>
            <form onSubmit={aboutState.handleAddAboutUs}>
              {aboutState.error && (
                <div className="modal-error">
                  <IconAlert /> {aboutState.error}
                </div>
              )}
              <div className="modal-body">
                <FormInput
                  id="about-title-add"
                  name="title"
                  label="Sarlavha"
                  value={aboutState.title}
                  onChange={(e) => aboutState.setTitle(e.target.value)}
                  placeholder="Masalan: Bizning jamoa"
                  error={aboutState.fieldErrors.title}
                  disabled={aboutState.isSubmitting}
                  autoFocus
                />

                <div className="form-group-custom">
                  <label htmlFor="about-text-add" className="form-group-custom__label">
                    Tavsif / Matn
                  </label>
                  <textarea
                    id="about-text-add"
                    className={`form-group-custom__textarea ${aboutState.fieldErrors.text ? 'form-group-custom__textarea--error' : ''}`}
                    rows="4"
                    value={aboutState.text}
                    onChange={(e) => aboutState.setText(e.target.value)}
                    placeholder="Batafsil ma'lumot kiriting..."
                    disabled={aboutState.isSubmitting}
                  />
                  {aboutState.fieldErrors.text && (
                    <span className="form-group-custom__error-msg">{aboutState.fieldErrors.text}</span>
                  )}
                </div>

                {/* Media Uploader Widget */}
                <div className="media-uploader-widget">
                  <span className="media-uploader-widget__label">Rasmlar to'plami</span>
                  
                  <div className="media-uploader-grid">
                    {/* Existing previews */}
                    {aboutState.mediaUrls.map((url, idx) => (
                      <div className="media-preview-card" key={idx}>
                        <img src={url} alt="preview" className="media-preview-card__img" />
                        <button
                          type="button"
                          className="media-preview-card__remove-btn"
                          title="O'chirish"
                          onClick={() => aboutState.handleRemoveImage(idx)}
                        >
                          <IconClose />
                        </button>
                      </div>
                    ))}

                    {/* Upload button card */}
                    <label className={`media-upload-trigger-card ${aboutState.isUploading ? 'media-upload-trigger-card--loading' : ''}`}>
                      {aboutState.isUploading ? (
                        <div className="dashboard-loading__spinner" />
                      ) : (
                        <>
                          <IconUpload />
                          <span>Rasm yuklash</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        disabled={aboutState.isUploading || aboutState.isSubmitting}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            aboutState.handleUploadImage(file);
                          }
                          e.target.value = ''; // Reset input selection
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button variant="outline" size="md" onClick={aboutState.closeModal} disabled={aboutState.isSubmitting}>
                  Bekor qilish
                </Button>
                <Button id="btn-submit-add-about" type="submit" variant="primary" size="md" isLoading={aboutState.isSubmitting}>
                  Qo'shish
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======== Modal: EDIT ABOUT US ======== */}
      {aboutState.activeModal === 'edit' && (
        <div className="modal-overlay" onClick={aboutState.closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={aboutState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Ma'lumot kartasini tahrirlash</h3>
              <p className="modal-subtitle">Sarlavha, tavsif va rasmlar to'plamini yangilang.</p>
            </div>
            <form onSubmit={aboutState.handleEditAboutUs}>
              {aboutState.error && (
                <div className="modal-error">
                  <IconAlert /> {aboutState.error}
                </div>
              )}
              <div className="modal-body">
                <FormInput
                  id="about-title-edit"
                  name="title"
                  label="Sarlavha"
                  value={aboutState.title}
                  onChange={(e) => aboutState.setTitle(e.target.value)}
                  placeholder="Masalan: Bizning jamoa"
                  error={aboutState.fieldErrors.title}
                  disabled={aboutState.isSubmitting}
                  autoFocus
                />

                <div className="form-group-custom">
                  <label htmlFor="about-text-edit" className="form-group-custom__label">
                    Tavsif / Matn
                  </label>
                  <textarea
                    id="about-text-edit"
                    className={`form-group-custom__textarea ${aboutState.fieldErrors.text ? 'form-group-custom__textarea--error' : ''}`}
                    rows="4"
                    value={aboutState.text}
                    onChange={(e) => aboutState.setText(e.target.value)}
                    placeholder="Batafsil ma'lumot kiriting..."
                    disabled={aboutState.isSubmitting}
                  />
                  {aboutState.fieldErrors.text && (
                    <span className="form-group-custom__error-msg">{aboutState.fieldErrors.text}</span>
                  )}
                </div>

                {/* Media Uploader Widget */}
                <div className="media-uploader-widget">
                  <span className="media-uploader-widget__label">Rasmlar to'plami</span>
                  
                  <div className="media-uploader-grid">
                    {/* Existing previews */}
                    {aboutState.mediaUrls.map((url, idx) => (
                      <div className="media-preview-card" key={idx}>
                        <img src={url} alt="preview" className="media-preview-card__img" />
                        <button
                          type="button"
                          className="media-preview-card__remove-btn"
                          title="O'chirish"
                          onClick={() => aboutState.handleRemoveImage(idx)}
                        >
                          <IconClose />
                        </button>
                      </div>
                    ))}

                    {/* Upload button card */}
                    <label className={`media-upload-trigger-card ${aboutState.isUploading ? 'media-upload-trigger-card--loading' : ''}`}>
                      {aboutState.isUploading ? (
                        <div className="dashboard-loading__spinner" />
                      ) : (
                        <>
                          <IconUpload />
                          <span>Rasm yuklash</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        disabled={aboutState.isUploading || aboutState.isSubmitting}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            aboutState.handleUploadImage(file);
                          }
                          e.target.value = ''; // Reset input selection
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Button variant="outline" size="md" onClick={aboutState.closeModal} disabled={aboutState.isSubmitting}>
                  Bekor qilish
                </Button>
                <Button id="btn-submit-edit-about" type="submit" variant="primary" size="md" isLoading={aboutState.isSubmitting}>
                  Yangilash
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======== Modal: CONFIRM DELETE ABOUT ======== */}
      {aboutState.activeModal === 'delete' && aboutState.selectedAbout && (
        <div className="modal-overlay" onClick={aboutState.closeModal}>
          <div className="modal-content modal-content--delete" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={aboutState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Ma'lumot kartasini o'chirish</h3>
              <p className="modal-subtitle">
                Haqiqatdan ham <strong>"{aboutState.selectedAbout.title}"</strong> ma'lumot kartasini o'chirib tashlamoqchimisiz?
                Bu amalni ortga qaytarib bo'lmaydi.
              </p>
            </div>
            {aboutState.error && (
              <div className="modal-error">
                <IconAlert /> {aboutState.error}
              </div>
            )}
            <div className="modal-footer">
              <Button variant="outline" size="md" onClick={aboutState.closeModal} disabled={aboutState.isSubmitting}>
                Bekor qilish
              </Button>
              <Button
                id="btn-submit-delete-about"
                variant="primary"
                size="md"
                isLoading={aboutState.isSubmitting}
                style={{ background: 'var(--clr-error)', boxShadow: 'none' }}
                onClick={aboutState.handleDeleteAboutUs}
              >
                Ha, o'chirilsin
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ======== Modal: ADD ORDER ======== */}
      {ordersState.activeModal === 'add' && (
        <div className="modal-overlay" onClick={ordersState.closeModal}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={ordersState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Yangi buyurtma yaratish</h3>
              <p className="modal-subtitle">Mijoz ma'lumotlari, tanlangan tadbirlar va moliyaviy hisob-kitoblarni shakllantiring.</p>
            </div>
            <form onSubmit={ordersState.handleAddOrder}>
              {ordersState.error && (
                <div className="modal-error">
                  <IconAlert /> {ordersState.error}
                </div>
              )}
              <div className="modal-body modal-body--grid">
                {/* Left Column */}
                <div className="modal-body-column">
                  <FormInput
                    id="order-person-add"
                    name="person"
                    label="Mijoz ismi (F.I.SH)"
                    value={ordersState.person}
                    onChange={(e) => ordersState.setPerson(e.target.value)}
                    placeholder="Masalan: Sardor Rahimov"
                    error={ordersState.fieldErrors.person}
                    disabled={ordersState.isSubmitting}
                    autoFocus
                  />

                  <FormInput
                    id="order-phone1-add"
                    name="phone1"
                    label="Asosiy telefon raqami"
                    value={ordersState.phone1}
                    onChange={(e) => ordersState.setPhone1(e.target.value)}
                    placeholder="Masalan: +998901234567"
                    error={ordersState.fieldErrors.phone1}
                    disabled={ordersState.isSubmitting}
                  />

                  <FormInput
                    id="order-phone2-add"
                    name="phone2"
                    label="Qo'shimcha telefon raqami"
                    value={ordersState.phone2}
                    onChange={(e) => ordersState.setPhone2(e.target.value)}
                    placeholder="Masalan: +998939876543"
                    error={ordersState.fieldErrors.phone2}
                    disabled={ordersState.isSubmitting}
                  />

                  <div className="form-group-custom">
                    <label htmlFor="order-date-add" className="form-group-custom__label">
                      Tadbir sanasi va vaqti
                    </label>
                    <input
                      id="order-date-add"
                      type="datetime-local"
                      className="form-input"
                      value={ordersState.eventDate}
                      onChange={(e) => ordersState.setEventDate(e.target.value)}
                      disabled={ordersState.isSubmitting}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="modal-body-column">
                  <FormInput
                    id="order-total-add"
                    name="totalPrice"
                    label="Jami buyurtma summasi (UZS)"
                    value={ordersState.totalPrice}
                    onChange={(e) => ordersState.setTotalPrice(e.target.value)}
                    placeholder="Masalan: 12000000"
                    error={ordersState.fieldErrors.totalPrice}
                    disabled={ordersState.isSubmitting}
                  />

                  <FormInput
                    id="order-advance-add"
                    name="advancePayment"
                    label="Avans to'lovi (Prepayment - UZS)"
                    value={ordersState.advancePayment}
                    onChange={(e) => ordersState.setAdvancePayment(e.target.value)}
                    placeholder="Masalan: 3000000"
                    error={ordersState.fieldErrors.advancePayment}
                    disabled={ordersState.isSubmitting}
                  />

                  <div className="form-group-custom">
                    <label htmlFor="order-desc-add" className="form-group-custom__label">
                      Qo'shimcha tafsilotlar / Izoh
                    </label>
                    <textarea
                      id="order-desc-add"
                      className="form-group-custom__textarea"
                      rows="3"
                      value={ordersState.description}
                      onChange={(e) => ordersState.setDescription(e.target.value)}
                      placeholder="Tadbir bo'yicha maxsus ko'rsatmalar..."
                      disabled={ordersState.isSubmitting}
                    />
                  </div>

                  <div className="form-group-custom">
                    <label className="toggle-switch-label" style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={ordersState.isDone}
                          onChange={(e) => ordersState.setIsDone(e.target.checked)}
                          disabled={ordersState.isSubmitting}
                        />
                        <span className="toggle-slider" />
                      </label>
                      <span style={{ fontSize: '14.5px', fontWeight: '600', color: 'var(--clr-text-dark)' }}>
                        Buyurtma bajarildi deb belgilansin
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Grid-span selection items */}
              <div className="modal-body modal-body--spans">
                {/* Event Types grid selection */}
                <div className="form-group-custom">
                  <label className="form-group-custom__label">Tadbir turlarini tanlang</label>
                  <div className="modal-events-selection-grid">
                    {eventsState.events.map((ev) => {
                      const isChecked = ordersState.eventIds.includes(ev.id);
                      return (
                        <button
                          type="button"
                          key={ev.id}
                          className={`modal-event-selection-btn ${isChecked ? 'modal-event-selection-btn--active' : ''}`}
                          onClick={() => ordersState.toggleEventId(ev.id)}
                          disabled={ordersState.isSubmitting}
                        >
                          <span className="selection-checkbox-indicator">{isChecked ? '✓' : ''}</span>
                          <span className="selection-text">{ev.title}</span>
                        </button>
                      );
                    })}
                  </div>
                  {ordersState.fieldErrors.eventIds && (
                    <span className="form-group-custom__error-msg">{ordersState.fieldErrors.eventIds}</span>
                  )}
                </div>

                {/* Required Things item builder */}
                <div className="form-group-custom">
                  <label className="form-group-custom__label">Sotib olinishi yoki tayyorlanishi kerak bo'lgan ishlar/narsalar</label>
                  <div className="todo-builder-input-row">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Masalan: 3 ta professional kamera, Zal bezaklari..."
                      value={ordersState.newThingInput}
                      onChange={(e) => ordersState.setNewThingInput(e.target.value)}
                      disabled={ordersState.isSubmitting}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          ordersState.addRequiredThing();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="todo-builder-add-btn"
                      onClick={ordersState.addRequiredThing}
                      disabled={ordersState.isSubmitting}
                    >
                      Qo'shish
                    </button>
                  </div>
                  
                  {ordersState.requiredThings.length > 0 && (
                    <div className="todo-builder-tags-grid">
                      {ordersState.requiredThings.map((thing, idx) => (
                        <div className="todo-builder-tag" key={idx}>
                          <span>{thing}</span>
                          <button
                            type="button"
                            className="todo-builder-tag__remove"
                            onClick={() => ordersState.removeRequiredThing(idx)}
                            disabled={ordersState.isSubmitting}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <Button variant="outline" size="md" onClick={ordersState.closeModal} disabled={ordersState.isSubmitting}>
                  Bekor qilish
                </Button>
                <Button id="btn-submit-add-order" type="submit" variant="primary" size="md" isLoading={ordersState.isSubmitting}>
                  Saqlash
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======== Modal: EDIT ORDER ======== */}
      {ordersState.activeModal === 'edit' && ordersState.selectedOrder && (
        <div className="modal-overlay" onClick={ordersState.closeModal}>
          <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={ordersState.closeModal} aria-label="Yopish">
              <IconClose />
            </button>
            <div className="modal-header">
              <h3 className="modal-title">Buyurtmani tahrirlash</h3>
              <p className="modal-subtitle">Mijoz tafsilotlarini, to'lov balanslarini va kerakli narsalar ro'yxatini tahrirlang.</p>
            </div>
            <form onSubmit={ordersState.handleEditOrder}>
              {ordersState.error && (
                <div className="modal-error">
                  <IconAlert /> {ordersState.error}
                </div>
              )}
              <div className="modal-body modal-body--grid">
                {/* Left Column */}
                <div className="modal-body-column">
                  <FormInput
                    id="order-person-edit"
                    name="person"
                    label="Mijoz ismi (F.I.SH)"
                    value={ordersState.person}
                    onChange={(e) => ordersState.setPerson(e.target.value)}
                    placeholder="Masalan: Sardor Rahimov"
                    error={ordersState.fieldErrors.person}
                    disabled={ordersState.isSubmitting}
                    autoFocus
                  />

                  <FormInput
                    id="order-phone1-edit"
                    name="phone1"
                    label="Asosiy telefon raqami"
                    value={ordersState.phone1}
                    onChange={(e) => ordersState.setPhone1(e.target.value)}
                    placeholder="Masalan: +998901234567"
                    error={ordersState.fieldErrors.phone1}
                    disabled={ordersState.isSubmitting}
                  />

                  <FormInput
                    id="order-phone2-edit"
                    name="phone2"
                    label="Qo'shimcha telefon raqami"
                    value={ordersState.phone2}
                    onChange={(e) => ordersState.setPhone2(e.target.value)}
                    placeholder="Masalan: +998939876543"
                    error={ordersState.fieldErrors.phone2}
                    disabled={ordersState.isSubmitting}
                  />

                  <div className="form-group-custom">
                    <label htmlFor="order-date-edit" className="form-group-custom__label">
                      Tadbir sanasi va vaqti
                    </label>
                    <input
                      id="order-date-edit"
                      type="datetime-local"
                      className="form-input"
                      value={ordersState.eventDate}
                      onChange={(e) => ordersState.setEventDate(e.target.value)}
                      disabled={ordersState.isSubmitting}
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="modal-body-column">
                  <FormInput
                    id="order-total-edit"
                    name="totalPrice"
                    label="Jami buyurtma summasi (UZS)"
                    value={ordersState.totalPrice}
                    onChange={(e) => ordersState.setTotalPrice(e.target.value)}
                    placeholder="Masalan: 12000000"
                    error={ordersState.fieldErrors.totalPrice}
                    disabled={ordersState.isSubmitting}
                  />

                  <FormInput
                    id="order-advance-edit"
                    name="advancePayment"
                    label="Avans to'lovi (Prepayment - UZS)"
                    value={ordersState.advancePayment}
                    onChange={(e) => ordersState.setAdvancePayment(e.target.value)}
                    placeholder="Masalan: 3000000"
                    error={ordersState.fieldErrors.advancePayment}
                    disabled={ordersState.isSubmitting}
                  />

                  <div className="form-group-custom">
                    <label htmlFor="order-desc-edit" className="form-group-custom__label">
                      Qo'shimcha tafsilotlar / Izoh
                    </label>
                    <textarea
                      id="order-desc-edit"
                      className="form-group-custom__textarea"
                      rows="3"
                      value={ordersState.description}
                      onChange={(e) => ordersState.setDescription(e.target.value)}
                      placeholder="Tadbir bo'yicha maxsus ko'rsatmalar..."
                      disabled={ordersState.isSubmitting}
                    />
                  </div>

                  <div className="form-group-custom">
                    <label className="toggle-switch-label" style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={ordersState.isDone}
                          onChange={(e) => ordersState.setIsDone(e.target.checked)}
                          disabled={ordersState.isSubmitting}
                        />
                        <span className="toggle-slider" />
                      </label>
                      <span style={{ fontSize: '14.5px', fontWeight: '600', color: 'var(--clr-text-dark)' }}>
                        Buyurtma bajarildi deb belgilansin
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Grid-span selection items */}
              <div className="modal-body modal-body--spans">
                {/* Event Types grid selection */}
                <div className="form-group-custom">
                  <label className="form-group-custom__label">Tadbir turlarini tanlang</label>
                  <div className="modal-events-selection-grid">
                    {eventsState.events.map((ev) => {
                      const isChecked = ordersState.eventIds.includes(ev.id);
                      return (
                        <button
                          type="button"
                          key={ev.id}
                          className={`modal-event-selection-btn ${isChecked ? 'modal-event-selection-btn--active' : ''}`}
                          onClick={() => ordersState.toggleEventId(ev.id)}
                          disabled={ordersState.isSubmitting}
                        >
                          <span className="selection-checkbox-indicator">{isChecked ? '✓' : ''}</span>
                          <span className="selection-text">{ev.title}</span>
                        </button>
                      );
                    })}
                  </div>
                  {ordersState.fieldErrors.eventIds && (
                    <span className="form-group-custom__error-msg">{ordersState.fieldErrors.eventIds}</span>
                  )}
                </div>

                {/* Required Things item builder */}
                <div className="form-group-custom">
                  <label className="form-group-custom__label">Sotib olinishi yoki tayyorlanishi kerak bo'lgan ishlar/narsalar</label>
                  <div className="todo-builder-input-row">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Masalan: 3 ta professional kamera, Zal bezaklari..."
                      value={ordersState.newThingInput}
                      onChange={(e) => ordersState.setNewThingInput(e.target.value)}
                      disabled={ordersState.isSubmitting}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          ordersState.addRequiredThing();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="todo-builder-add-btn"
                      onClick={ordersState.addRequiredThing}
                      disabled={ordersState.isSubmitting}
                    >
                      Qo'shish
                    </button>
                  </div>
                  
                  {ordersState.requiredThings.length > 0 && (
                    <div className="todo-builder-tags-grid">
                      {ordersState.requiredThings.map((thing, idx) => (
                        <div className="todo-builder-tag" key={idx}>
                          <span>{thing}</span>
                          <button
                            type="button"
                            className="todo-builder-tag__remove"
                            onClick={() => ordersState.removeRequiredThing(idx)}
                            disabled={ordersState.isSubmitting}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <Button variant="outline" size="md" onClick={ordersState.closeModal} disabled={ordersState.isSubmitting}>
                  Bekor qilish
                </Button>
                <Button id="btn-submit-edit-order" type="submit" variant="primary" size="md" isLoading={ordersState.isSubmitting}>
                  Yangilash
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <nav className="dashboard-mobile-tabs" aria-label="Asosiy bo'limlar">
        <button
          type="button"
          className={`dashboard-mobile-tabs__btn ${activeTab === 'events' ? 'dashboard-mobile-tabs__btn--active' : ''}`}
          onClick={() => { playSoftPop(); setActiveTab('events'); }}
        >
          <IconCalendar />
          <span>Tadbirlar</span>
        </button>
        <button
          type="button"
          className={`dashboard-mobile-tabs__btn ${activeTab === 'requests' ? 'dashboard-mobile-tabs__btn--active' : ''}`}
          onClick={() => { playSoftPop(); setActiveTab('requests'); }}
        >
          <span className="dashboard-mobile-tabs__icon-wrap">
            <IconMail />
            {requestsState.unreadCount > 0 && (
              <span className="dashboard-mobile-tabs__badge">{requestsState.unreadCount > 9 ? '9+' : requestsState.unreadCount}</span>
            )}
          </span>
          <span>So&apos;rovlar</span>
        </button>
        <button
          type="button"
          className={`dashboard-mobile-tabs__btn ${activeTab === 'about' ? 'dashboard-mobile-tabs__btn--active' : ''}`}
          onClick={() => { playSoftPop(); setActiveTab('about'); }}
        >
          <IconInfo />
          <span>Biz haqimizda</span>
        </button>
        <button
          type="button"
          className={`dashboard-mobile-tabs__btn ${activeTab === 'orders' ? 'dashboard-mobile-tabs__btn--active' : ''}`}
          onClick={() => { playSoftPop(); setActiveTab('orders'); }}
        >
          <IconBriefcase />
          <span>Buyurtmalar</span>
        </button>
      </nav>

      {orderPopover && (() => {
        const po = ordersState.orders.find((o) => o.id === orderPopover.orderId);
        if (!po) return null;
        const popW = 288;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 700;
        const left = Math.min(Math.max(8, orderPopover.rect.left), vw - popW - 8);
        const maxPanelH = 340;
        let top = orderPopover.rect.bottom + 8;
        if (top + maxPanelH > vh - 12) {
          top = Math.max(12, orderPopover.rect.top - Math.min(maxPanelH, 280) - 8);
        }
        const popId = `order-pop-${po.id}-${orderPopover.type}`;
        const bal = po.totalPrice - (po.advancePayment || 0);
        const paid = bal <= 0;
        const evs = Array.isArray(po.eventDtos) ? po.eventDtos : [];
        const todos = Array.isArray(po.requiredThings) ? po.requiredThings : [];

        let title = '';
        let body = null;
        if (orderPopover.type === 'client') {
          title = 'Mijoz';
          body = (
            <>
              <div className="order-popover-kv">
                <span className="order-popover-kv__k">F.I.Sh</span>
                <strong className="order-popover-kv__v">{po.person}</strong>
              </div>
              <div className="order-popover-kv">
                <span className="order-popover-kv__k">Asosiy tel.</span>
                <a className="order-popover-kv__v order-popover-kv__link" href={`tel:${po.phone1}`}>
                  <IconPhone /> {po.phone1}
                </a>
              </div>
              {po.phone2 ? (
                <div className="order-popover-kv">
                  <span className="order-popover-kv__k">Qo'shimcha</span>
                  <a className="order-popover-kv__v order-popover-kv__link" href={`tel:${po.phone2}`}>
                    <IconPhone /> {po.phone2}
                  </a>
                </div>
              ) : null}
              {po.description ? (
                <p className="order-popover-note">{po.description}</p>
              ) : null}
            </>
          );
        } else if (orderPopover.type === 'finance') {
          title = 'Hisob-kitob';
          body = (
            <div className="order-financial-info order-financial-info--popover">
              <div className="financial-row">
                <span className="fin-label">Jami:</span>
                <span className="fin-value">{po.totalPrice.toLocaleString()} UZS</span>
              </div>
              <div className="financial-row">
                <span className="fin-label">Avans:</span>
                <span className="fin-value fin-value--advance">{po.advancePayment ? po.advancePayment.toLocaleString() : 0} UZS</span>
              </div>
              <div className="financial-row financial-row--balance">
                <span className="fin-label">Qoldiq:</span>
                <span className={`fin-value ${paid ? 'fin-value--paid' : 'fin-value--outstanding'}`}>
                  {bal.toLocaleString()} UZS
                </span>
              </div>
            </div>
          );
        } else if (orderPopover.type === 'todos') {
          title = 'Ishlar ro\'yxati';
          body = todos.length > 0 ? (
            <ul className="order-popover-todo-ul">
              {todos.map((thing, idx) => (
                <li key={idx} className="order-popover-todo-li">
                  <span className="todo-bullet" aria-hidden="true">✓</span>
                  {thing}
                </li>
              ))}
            </ul>
          ) : (
            <p className="order-popover-empty">Ro&apos;yxat bo&apos;sh</p>
          );
        } else if (orderPopover.type === 'events') {
          title = 'Tanlangan tadbirlar';
          body = evs.length > 0 ? (
            <ul className="order-popover-events-ul">
              {evs.map((e) => (
                <li key={e.id} className="order-popover-events-li">{e.title}</li>
              ))}
            </ul>
          ) : (
            <p className="order-popover-empty">Tadbir tanlanmagan</p>
          );
        }

        return (
          <>
            <div
              className="order-popover-backdrop"
              role="presentation"
              onClick={() => setOrderPopover(null)}
            />
            <div
              id={popId}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`${popId}-title`}
              className="order-popover-panel"
              style={{ top, left, width: popW }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="order-popover-panel__head">
                <h3 id={`${popId}-title`} className="order-popover-panel__title">{title}</h3>
                <button
                  type="button"
                  className="order-popover-panel__close"
                  onClick={() => setOrderPopover(null)}
                  aria-label="Yopish"
                >
                  <IconClose />
                </button>
              </div>
              <div className="order-popover-panel__body">
                {body}
              </div>
              <div className="order-popover-panel__foot">
                <button
                  type="button"
                  className="order-popover-link-edit"
                  onClick={() => {
                    ordersState.openEditModal(po);
                    setOrderPopover(null);
                  }}
                >
                  Tahrirlash
                </button>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
